import { Flame, Pencil, Wheat } from "lucide-react";
import { Card } from "../ui/Card";
import { cn } from "../../lib/cn";
import { formatNumber } from "../../lib/format";
import {
  latestAdjustmentKcal,
  macroComposition,
  type CheckinView,
  type MacroSegment,
  type PlanProfile,
} from "../../screens/plan-utils";
import { formatAdjustmentKcal, type EngineSnapshot } from "../progress/utils";

const EDIT_BUTTON = cn(
  "flex min-h-[44px] items-center gap-1.5 rounded-xl border border-border bg-surface px-3 py-2",
  "text-xs font-semibold text-accent select-none transition-all duration-200 ease-out active:scale-[0.98]",
);

const SEGMENT_COLORS: Record<MacroSegment["key"], string> = {
  protein: "bg-accent",
  carbs: "bg-amber-500",
  fat: "bg-teal-600",
};

const GRAM_TEST_IDS: Record<MacroSegment["key"], string> = {
  protein: "target-protein",
  carbs: "target-carbs",
  fat: "target-fat",
};

export interface DailyTargetsCardProps {
  readonly profile: PlanProfile;
  readonly snapshot: EngineSnapshot;
  readonly checkins: readonly CheckinView[];
  readonly onEditPreferences: () => void;
}

export function DailyTargetsCard({ profile, snapshot, checkins, onEditPreferences }: DailyTargetsCardProps) {
  const kcal = profile.dailyGoalKcal ?? snapshot.targetKcal ?? undefined;
  const segments = macroComposition({
    proteinG: profile.dailyProteinG,
    carbsG: profile.dailyCarbsG,
    fatG: profile.dailyFatG,
  });
  const adjustment = latestAdjustmentKcal(checkins);

  return (
    <Card className="flex flex-col gap-4 p-4" data-testid="plan-targets-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="rounded-full bg-accent-light p-2 text-accent">
            <Flame size={18} strokeWidth={2} />
          </div>
          <h3 className="text-base font-semibold text-text">Daily targets</h3>
        </div>
        <button type="button" className={EDIT_BUTTON} data-testid="edit-preferences-btn" onClick={onEditPreferences}>
          <Pencil size={14} strokeWidth={2} />
          Edit programme preferences
        </button>
      </div>

      <div className="flex flex-wrap items-end gap-x-3 gap-y-1.5">
        <p className="text-3xl font-bold leading-none text-accent" data-testid="target-kcal">
          {kcal !== undefined ? `${formatNumber(kcal)} kcal` : "—"}
        </p>
        <p className="pb-0.5 text-sm font-medium text-text-muted">per day</p>
        {adjustment !== null && (
          <span
            className="rounded-full bg-accent-light px-2.5 py-1 text-[11px] font-semibold text-accent-dark"
            data-testid="target-adjustment-chip"
          >
            {formatAdjustmentKcal(adjustment)} since your last review
          </span>
        )}
      </div>

      {segments !== null ? (
        <div className="flex flex-col gap-2.5">
          <div
            className="flex h-3 w-full overflow-hidden rounded-full bg-muted"
            data-testid="macro-strip"
            role="img"
            aria-label="Calorie split across protein, carbs, and fat"
          >
            {segments.map((segment) => (
              <div
                key={segment.key}
                className={cn("plan-fill-grow h-full", SEGMENT_COLORS[segment.key])}
                style={{ width: `${Math.round(segment.fraction * 1000) / 10}%` }}
                data-testid={`macro-segment-${segment.key}`}
              />
            ))}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {segments.map((segment) => (
              <span key={segment.key} className="flex items-center gap-1.5 text-[11px] font-medium text-text-muted">
                <span aria-hidden className={cn("inline-block h-2 w-2 rounded-full", SEGMENT_COLORS[segment.key])} />
                {segment.label} {Math.round(segment.fraction * 100)}%
              </span>
            ))}
          </div>
          <dl className="grid grid-cols-3 gap-2">
            {segments.map((segment) => (
              <div key={segment.key} className="rounded-xl bg-muted/60 px-2.5 py-2">
                <dt className="text-[10px] font-medium uppercase tracking-wide text-text-muted">{segment.label}</dt>
                <dd className="mt-0.5 text-sm font-semibold text-text" data-testid={GRAM_TEST_IDS[segment.key]}>
                  {formatNumber(segment.grams)} g
                </dd>
              </div>
            ))}
          </dl>
        </div>
      ) : (
        <p className="text-sm leading-relaxed text-text-muted" data-testid="macro-pending">
          Not enough data yet — your macro split appears once targets are calculated.
        </p>
      )}

      {profile.dailyFiberG !== undefined && (
        <div className="flex items-center gap-2 rounded-xl bg-muted/60 px-3 py-2">
          <Wheat size={14} className="shrink-0 text-text-muted" aria-hidden />
          <p className="text-xs text-text-muted">
            Fibre{" "}
            <span className="font-semibold text-text" data-testid="target-fibre">
              {formatNumber(profile.dailyFiberG)} g
            </span>{" "}
            daily — tracked separately from the macro split.
          </p>
        </div>
      )}

      <p className="text-xs leading-relaxed text-text-muted">
        Targets are calculated by your plan engine and update at weekly check-ins — they cannot be edited directly.
      </p>
    </Card>
  );
}
